/**
 * Category Mapper
 *
 * Converts between API responses and Domain entities.
 * Handles type conversions, data transformations, and validation.
 */

import { Category, CategoryProps } from "../../domain/entities/Category";

/**
 * Represents the shape of category data from the API/database
 * This is what we get from the Orval-generated hooks
 */
export interface CategoryDTO {
  id: string;
  name: string;
  parentId?: string | null;
  level: number;
  displayOrder?: number;
  children?: CategoryDTO[];
}

export class CategoryMapper {
  /**
   * Convert API/DTO to Domain Entity
   */
  static toDomain(dto: CategoryDTO): Category {
    return new Category({
      id: dto.id,
      name: dto.name,
      parentId: dto.parentId ?? null,
      level: dto.level,
    });
  }

  /**
   * Convert multiple DTOs to Domain Entities
   */
  static toDomainList(dtos: CategoryDTO[]): Category[] {
    return dtos.map((dto) => this.toDomain(dto));
  }

  /**
   * Flatten a tree structure into a list of categories
   * ✅ Type-safe: properly sets parentId from tree hierarchy
   */
  static flattenTree(tree: CategoryDTO[]): Category[] {
    const flattened: Category[] = [];

    const traverse = (
      nodes: CategoryDTO[],
      parentId: string | null = null,
    ): void => {
      if (!Array.isArray(nodes)) return;

      nodes.forEach((node: CategoryDTO) => {
        // ✅ Create domain entity with correct parentId
        flattened.push(
          new Category({
            id: node.id,
            name: node.name,
            parentId, // ✅ Set from parameter, not from node
            level: node.level,
          }),
        );

        // ✅ Recursively flatten children with this node as parent
        if (
          node.children &&
          Array.isArray(node.children) &&
          node.children.length > 0
        ) {
          traverse(node.children, node.id); // ✅ Pass node.id as parentId
        }
      });
    };

    traverse(tree, null); // ✅ Root has parentId = null
    return flattened;
  }

  /**
   * Convert Domain Entity to API/DTO format
   */
  static toPersistence(category: Category): Omit<CategoryDTO, "children"> {
    return {
      id: category.id,
      name: category.name,
      parentId: category.parentId,
      level: category.level,
    };
  }

  /**
   * Create DTO for API create/update requests
   */
  static toCreateDTO(name: string, parentId: string | null, level: number) {
    return {
      name,
      parentId,
      level,
      displayOrder: 0, // Default display order
    };
  }
}
